import { useTranslation } from 'react-i18next';
import { Md } from '../components/Md';

function Command({
  name,
  i18nKey,
  usage,
  options,
}: {
  name: string;
  i18nKey: string;
  usage: string;
  options?: { flag: string; descKey: string }[];
}) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
      <h3 className="font-mono text-lg font-semibold text-cyan-400">{name}</h3>
      <p className="mt-2 text-sm text-slate-400">{t(`cli.commands.${i18nKey}.description`)}</p>
      <div dir="ltr" className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-sm text-slate-300">
        <span className="text-slate-500">$</span> {usage}
      </div>
      {options && options.length > 0 && (
        <div className="mt-4">
          <table className="w-full text-sm">
            <tbody>
              {options.map((opt) => (
                <tr key={opt.flag} className="border-t border-slate-800/50">
                  <td dir="ltr" className="py-2 pr-4 font-mono text-cyan-400 whitespace-nowrap">
                    {opt.flag}
                  </td>
                  <td className="py-2 text-slate-400">{t(`cli.commands.${i18nKey}.options.${opt.descKey}`)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const commands = [
  {
    name: 'login',
    i18nKey: 'login',
    usage: 'quicktl login',
    options: [
      { flag: '--email <email>', descKey: 'email' },
      { flag: '--password <pass>', descKey: 'password' },
      { flag: '--base-url <url>', descKey: 'baseUrl' },
      { flag: '--no-interactive', descKey: 'noInteractive' },
    ],
  },
  {
    name: 'init',
    i18nKey: 'init',
    usage: 'quicktl init',
    options: [
      { flag: '--project <id>', descKey: 'project' },
      { flag: '--source-path <path>', descKey: 'sourcePath' },
      { flag: '--target-path <pattern>', descKey: 'targetPath' },
      { flag: '--no-interactive', descKey: 'noInteractive' },
    ],
  },
  {
    name: 'translate',
    i18nKey: 'translate',
    usage: 'quicktl translate',
    options: [
      { flag: '-b, --branch <name>', descKey: 'branch' },
      { flag: '-l, --locales <list>', descKey: 'locales' },
      { flag: '--head', descKey: 'head' },
    ],
  },
  {
    name: 'verify',
    i18nKey: 'verify',
    usage: 'quicktl verify',
    options: [
      { flag: '-b, --branch <name>', descKey: 'branch' },
      { flag: '-l, --locales <list>', descKey: 'locales' },
    ],
  },
  {
    name: 'merge',
    i18nKey: 'merge',
    usage: 'quicktl merge -b feature/onboarding -t main',
    options: [
      { flag: '-b, --branch <name>', descKey: 'branch' },
      { flag: '-t, --target <name>', descKey: 'target' },
    ],
  },
  {
    name: 'compare',
    i18nKey: 'compare',
    usage: 'quicktl compare -b feature/onboarding -t main',
    options: [
      { flag: '-b, --branch <name>', descKey: 'branch' },
      { flag: '-t, --target <name>', descKey: 'target' },
    ],
  },
  {
    name: 'tm list',
    i18nKey: 'tmList',
    usage: 'quicktl tm list --locale fr',
    options: [
      { flag: '-l, --locale <code>', descKey: 'locale' },
      { flag: '-q, --query <text>', descKey: 'query' },
      { flag: '-p, --page <n>', descKey: 'page' },
      { flag: '-s, --page-size <n>', descKey: 'pageSize' },
    ],
  },
  {
    name: 'tm set',
    i18nKey: 'tmSet',
    usage: 'quicktl tm set --source-locale en --target-locale fr --source "Hello" --translation "Bonjour"',
    options: [
      { flag: '--source-locale <code>', descKey: 'sourceLocale' },
      { flag: '--target-locale <code>', descKey: 'targetLocale' },
      { flag: '--source <text>', descKey: 'source' },
      { flag: '--translation <text>', descKey: 'translation' },
    ],
  },
  {
    name: 'tm delete',
    i18nKey: 'tmDelete',
    usage: 'quicktl tm delete <id>',
  },
];

export default function CLIPage() {
  const { t } = useTranslation();

  return (
    <div className="mx-auto max-w-4xl px-6 py-20 md:py-28">
      <h1 className="font-display text-3xl font-bold tracking-tight md:text-4xl">{t('cli.heading')}</h1>
      <p className="mt-4 text-slate-400">
        {t('cli.description')}
      </p>

      <div className="mt-6 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-lg font-semibold">{t('cli.installation')}</h2>
        <div dir="ltr" className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-sm text-slate-300">
          <span className="text-slate-500">$</span> npm install -g @quicktl/cli
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-lg font-semibold">{t('cli.quickStart')}</h2>
        <div dir="ltr" className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-sm">
          <code className="text-slate-300">
            <span className="text-slate-500">$</span> quicktl login
            <br />
            <span className="text-slate-500">$</span> quicktl init
            <br />
            <span className="text-slate-500">$</span> quicktl translate
          </code>
        </div>
        <p className="mt-3 text-sm text-slate-400">
          {t('cli.quickStartNote')}
        </p>
      </div>

      <h2 className="mt-14 font-display text-2xl font-bold">{t('cli.commandsHeading')}</h2>
      <div className="mt-6 space-y-6">
        {commands.map((cmd) => (
          <Command key={cmd.name} {...cmd} />
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-lg font-semibold">{t('cli.ciMode.heading')}</h2>
        <Md as="p" className="mt-2 text-sm text-slate-400">
          {t('cli.ciMode.description')}
        </Md>
        <div dir="ltr" className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-sm text-slate-300">
          <span className="text-slate-500">{t('cli.ciMode.comment')}</span>
          <br />
          <span className="text-slate-500">$</span> quicktl translate --no-interactive --branch $GITHUB_HEAD_REF
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-800/80 bg-slate-900/50 p-6">
        <h2 className="font-display text-lg font-semibold">{t('cli.configuration.heading')}</h2>
        <Md as="p" className="mt-2 text-sm text-slate-400">
          {t('cli.configuration.description')}
        </Md>
        <pre dir="ltr" className="mt-3 rounded-xl border border-slate-800/80 bg-slate-950 px-4 py-3 font-mono text-sm">
          <code className="text-slate-300">
            {'{\n'}
            {'  '}<span className="text-cyan-400">"project"</span>: <span className="text-emerald-400">"your-project-id"</span>,{'\n'}
            {'  '}<span className="text-cyan-400">"sourcePath"</span>: <span className="text-emerald-400">"locales/en.json"</span>,{'\n'}
            {'  '}<span className="text-cyan-400">"targetPath"</span>: <span className="text-emerald-400">"locales/&#123;locale&#125;.json"</span>{'\n'}
            {'}'}
          </code>
        </pre>
        <Md as="p" className="mt-3 text-sm text-slate-400">
          {t('cli.configuration.credentialsNote')}
        </Md>
      </div>
    </div>
  );
}
